import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AggregatioRequestInterface } from '@app/models/aggregation.interface';
import { Constants } from '@app/models/constants.class';
import {
  AccountInterface,
  EntityInterface,
} from '@app/models/entity.interface';
import { SlideStates } from '@app/models/slide';
import { CategorizerService } from '@app/services/categorizer.service';
import { EntityService } from '@app/services/entity.service';
import { Psd2Service } from '@app/services/psd2.service';
import { TealiumService } from '@app/services/tealium.service';
import { AgEntitiesAdviseModalComponent } from '../ag-entities-advise-modal/ag-entities-advise-modal.component';
import { WindowMessagesService } from '@app/services/window-messages.service';

// type entitieStatus = 'success' | 'error' | 'loading';

@Component({
  standalone: false,
  selector: 'app-ag-entities-progress',
  templateUrl: './ag-entities-progress.component.html',
  styleUrls: ['./ag-entities-progress.component.scss'],
})
export class AgEntitiesProgressComponent implements OnInit, OnDestroy {
  public entitiesToAggregate: EntityInterface[] = [];
  public agregatedEntities!: EntityInterface[];
  public entitiesLength = 0;
  public SlideStates = SlideStates;
  public selectedSlide = -1;
  public step = 1;
  public error = 0;
  public doingAggregation = false;
  public aggregationDone = false;
  public endSession = false;
  public currentCall: Promise<void> | undefined = undefined;

  private _entitiesPollingInterval: any;
  public addMoreBanks = false;
  public isDeleteModalOpen = false;
  public entityToDelete: EntityInterface | null = null;
  public isToastVisible = false;
  public isErrorToastVisible = false;
  public toastEntityName = '';
  constructor(
    private _entityService: EntityService,
    private _router: Router,
    private readonly _psd2Service: Psd2Service,
    private readonly _tealiumService: TealiumService,
    private readonly _categorizerService: CategorizerService,
    private _dialog: MatDialog,
    private readonly _windowMessagesService: WindowMessagesService,
  ) {}

  ngOnInit(): void {
    let entitiesMoreBanks: any = [];
    let isGoBack = false;

    let selectBanks = sessionStorage.getItem('selectEntitiesBanks');
    if (selectBanks) {
      try {
        entitiesMoreBanks = JSON.parse(selectBanks);
      } catch (e) {
        console.error(
          'Error parsing selectEntitiesBanks from sessionStorage',
          e,
        );
        entitiesMoreBanks = [];
      }
    }

    let goBack = sessionStorage.getItem('goBack');
    if (goBack) {
      try {
        isGoBack = JSON.parse(goBack);
      } catch (e) {
        console.error('Error parsing goBack from sessionStorage', e);
        isGoBack = false;
      }
    }
    if (
      entitiesMoreBanks &&
      entitiesMoreBanks[0] != undefined &&
      entitiesMoreBanks[0] != null &&
      isGoBack
    ) {
      this.entitiesToAggregate = entitiesMoreBanks;
    } else {
      this.entitiesToAggregate =
        this._entityService.getSelectedEntities() || [];
    }
    this.entitiesLength = this.entitiesToAggregate.length;
    this.incrementStep();
  }

  ngOnDestroy(): void {
    sessionStorage.removeItem('goBack');
    clearInterval(this._entitiesPollingInterval);
  }

  toggleSlideState(index: any): void {
    if (this.selectedSlide === index) {
      this.selectedSlide = -1;
    } else {
      this.selectedSlide = index;
    }
  }

  /**
   * Gets delete modal title
   */
  get deleteModalTitle(): string {
    return `¿Eliminar agregación de ${this.entityToDelete?.name}?`;
  }

  /**
   * Opens delete modal
   * @param entity
   */
  openDeleteModal(entity: EntityInterface): void {
    this.entityToDelete = entity;
    this.isDeleteModalOpen = true;
  }

  /**
   * Closes delete modal
   */
  closeDeleteModal(): void {
    this.isDeleteModalOpen = false;
    this.entityToDelete = null;
  }

  /**
   * Determines whether delete modal button click on
   * @param event
   */
  onDeleteModalButtonClick(event: CustomEvent): void {
    if (event.detail === 'secondary') {
      this.closeDeleteModal();
    }
  }

  /**
   * Confirms delete: calls the appropriate DELETE endpoint based on customer type,
   * then closes the modal and shows a success or error toast.
   */
  confirmDelete(): void {
    this.toastEntityName = this.entityToDelete?.name || '';
    const expedientCode = sessionStorage.getItem('prop-unique-id') || '';
    const entityCode = this.entityToDelete?.code || '';

    this._categorizerService.deleteBank(expedientCode, entityCode)
      .then(() => {
        this.entitiesToAggregate = this.entitiesToAggregate.filter(
          (e) => e.code !== entityCode,
        );
        this.agregatedEntities = this.agregatedEntities.filter(
          (e) => e.code !== entityCode,
        );
        this.closeDeleteModal();
        this.getEntities();
        this.isToastVisible = true;
        setTimeout(() => { this.isToastVisible = false; }, 4000);
      })
      .catch(() => {
        this.closeDeleteModal();
        this.isErrorToastVisible = true;
        setTimeout(() => { this.isErrorToastVisible = false; }, 4000);
      });
  }

  incrementStep(): void {
    this.step++;
    this.initAggregationProcess()
      .then(() => {
        this.doingAggregation = false;
        this.aggregationDone = true;
      })
      .finally(() => this.initEntitiesPolling());
  }

  initAggregationProcess(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let counter = 0;
      this.entitiesToAggregate.forEach(async (entity: EntityInterface) => {
        this.aggregationConsent(entity).finally(() => {
          if (this.entitiesToAggregate.length - 1 > counter) {
            counter++;
          } else {
            resolve();
          }
        });
      });
    });
  }

  aggregationConsent(entity: EntityInterface): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      entity.status = 'loading';
      const aggregationRequest: AggregatioRequestInterface = {};
      const products = entity.products || [];
      aggregationRequest.aspspcode = entity.code;
      aggregationRequest.products = products
        .filter((value) => value.type === Constants.entityType.psd2)
        .map((value) => value.name || '');
      aggregationRequest.consentCode = entity.consentCode;
      aggregationRequest.expedientCode =
        sessionStorage.getItem('prop-unique-id') || '';

      if (this.checkIsInCurrentAggregatedEntities(entity.code!)) {
        resolve();
        return;
      }

      this.currentCall = this._psd2Service
        .postAggregationConsent(aggregationRequest, 'consentId')
        .then(() => {
          // AñadircategorizeEntitty para obtener los datos de cuenta
          this.tealiumAggregationComplete(entity);
          entity.status = 'oncourse';
          resolve();
        })
        .catch((errorResponse) => {
          this.manageErrorResponse(errorResponse);
          entity.status = 'error';
          reject();
        });
    });
  }

  /**
   * This method gets entities with current status, interval will stop when
   * no entity remains with "oncourse" status.
   */
  initEntitiesPolling(): void {
    if (!this._entitiesPollingInterval) {
      this.getEntities();
      this._entitiesPollingInterval = setInterval(() => {
        this.getEntities();
      }, 2000);
    }
  }

  getEntities(): void {
    const expedientCode = sessionStorage.getItem('prop-unique-id') || '';
    const isOnlySantander: boolean = JSON.parse(sessionStorage.getItem('onlySantander')!) ?? false;
    this._categorizerService
      .getEntities(expedientCode, isOnlySantander)
      .then((entities) => {
        sessionStorage.setItem(
          'currentAggregatedEntities',
          JSON.stringify(entities),
        ); // Current state saved in SS.
        this.agregatedEntities = this.filterSantanderCode(entities);
        this.agregatedEntities = this.getMergedEntities(this.agregatedEntities);
        this.agregatedEntities = this.removeDuplicateEntities(
          this.agregatedEntities,
        );
        // If no oncourse status, clear polling interval.
        !this.hasOnCourseStatus(this.agregatedEntities) &&
          clearInterval(this._entitiesPollingInterval);
      });
  }

  hasOnCourseStatus(aggregatedEntities: any): boolean {
    return aggregatedEntities.some(
      (entity: any) =>
        entity.status === Constants.statusNames.oncourse ||
        entity.status === Constants.statusNames.validating,
    );
  }

  /**
   * @deprecated There's no entity status update currently.
   * @param aggregatedEntities
   */
  updateEntityStatus(aggregatedEntities: any[]): void {
    aggregatedEntities.forEach((aggregatedEntity) => {
      let entity = this.entitiesToAggregate.find(
        (entity: EntityInterface) => entity.code === aggregatedEntity.code,
      );
      if (entity) {
        entity.status = aggregatedEntity.status;
        entity.accounts = [];
        if (aggregatedEntity.accounts) {
          aggregatedEntity.accounts.forEach((account: AccountInterface) => {
            const accountToPush = {
              currency: account.currency,
              iban: this.hideIban(account.iban!),
              balance: account.balance,
              productName: account.productName,
            };
            entity!.accounts!.push(accountToPush);
          });
        }
      }

      // If no oncourse status, clear polling interval.
      !this.hasOnCourseStatus(aggregatedEntities) &&
        clearInterval(this._entitiesPollingInterval);
    });
  }

  hideIban(iban: string): string {
    return '*' + iban.slice(-4);
  }

  navigateToEntities(): void {
    this.addMoreBanks = true;
    sessionStorage.setItem('addMoreBanks', JSON.stringify(this.addMoreBanks));
    sessionStorage.setItem(
      'selectEntitiesBanks',
      JSON.stringify(this.agregatedEntities),
    );
    //this._entityService.removeSelectedEntities();
    this._router.navigate(['/']);
  }

  tealiumAggregationComplete(entity: EntityInterface): void {
    this._tealiumService.sendPageLoad({
      page_name:
        '/PSD2/agregar_banco/conexión_resumen/' + `${entity.type || ''}`,
      page_title:
        'PSD2 agregar banco conexión resumen ' + `${entity.type || ''}`,
      page_type: 'thank you',
      product_scope: 'operativa_no_financiera',
      product_category: 'PSD2',
      product_name: 'crédito digital',
      page_category: 'servicing',
      added_bank: entity.name,
    });
  }

  manageErrorResponse(errorResponse: any): void {
    this.currentCall = undefined;
    this.step = -1;
    this.error = 500;
    this._tealiumService.sendError({
      category: 'Server error',
      action: 'error',
      data: {
        cod_error: errorResponse.status,
        desc_error: 'popup window',
      },
    });
  }

  hasAllErrors(): boolean {
    return (
      this.agregatedEntities &&
      this.agregatedEntities.every(
        (entity: EntityInterface) => entity.status === 'error',
      )
    );
  }

  hasAllLoading(): boolean {
    return (
      this.agregatedEntities &&
      this.agregatedEntities.every(
        (entity: EntityInterface) => entity.status === 'oncourse',
      )
    );
  }

  /**
   * Determines whether any loading or in progress has
   * @returns true if any loading or in progress
   */
  hasAnyLoadingOrInProgress(): boolean {
    const hasAnyLoading: boolean =
      this.agregatedEntities &&
      this.agregatedEntities.some((entity: EntityInterface) =>
        [
          'loading',
          Constants.statusNames.oncourse,
          Constants.statusNames.validating,
          'oncourse',
          'VALIDATING',
        ].includes((entity.status as string) || ''),
      );
    return hasAnyLoading;
  }

  hasAllSuccess(): boolean {
    return (
      this.agregatedEntities &&
      this.agregatedEntities.every(
        (entity: EntityInterface) => entity.status === 'OK',
      )
    );
  }
  hasAnySuccess(): boolean {
    return (
      this.agregatedEntities &&
      this.agregatedEntities.some(
        (entity: EntityInterface) =>
          entity.status === 'OK' || entity.status === 'success',
      )
    );
  }

  /**
   * Determines whether submit request can
   * @returns true if submit request
   */
  canSubmitRequest(): boolean {
    if (!this.agregatedEntities || this.agregatedEntities.length === 0) {
      return false;
    }
    return !this.hasAnyLoadingOrInProgress() && this.hasAnySuccess();
  }

  back(): void {
    this.addMoreBanks = true;
    sessionStorage.setItem('addMoreBanks', JSON.stringify(this.addMoreBanks));
    sessionStorage.setItem(
      'selectEntitiesBanks',
      JSON.stringify(this.agregatedEntities),
    );
    //this._entityService.removeSelectedEntities();
    this._router.navigate(['/']);
  }

  continue(): void {
    this._dialog
      .open(AgEntitiesAdviseModalComponent, {
        data: {
          primary: this.hasAllSuccess(),
        },
      })
      .afterClosed()
      .subscribe((data: any) => {
        if (data?.action === 'addMore') {
          this.navigateToEntities();
        } else if (data?.action === 'finish') {
          sessionStorage.setItem('finishProcess', 'true');
          this.navigateToEntities();
        }
      });
  }

  retry(id: number, index: number): void {
    sessionStorage.setItem('retryId', JSON.stringify(id));
    sessionStorage.setItem('retryIndex', JSON.stringify(index));
    sessionStorage.setItem('retry', JSON.stringify(true));
    this._router.navigate(['/loader']);
  }

  /**
   * @description cancel
   */
  cancel(): void {
    this._windowMessagesService.cancel();
    this.endSession = true;
  }

  filterSantanderCode(agregatedEntities: EntityInterface[]): EntityInterface[] {
    return agregatedEntities.filter(
      (entity: EntityInterface) => entity.code !== Constants.santanderCode,
    );
  }

  /**
   * If entity to agregate exists in agregated entity, remove from the entityToAgregate array.
   * @param agregatedEntities EntityInterface[] Api entities received
   * @returns EntityInterface[] Merged arrays
   */
  getMergedEntities(agregatedEntities: EntityInterface[]): EntityInterface[] {
    const copyEntitiesToAggregate = [...this.entitiesToAggregate];
    for (var i = 0; i < agregatedEntities.length; i++) {
      for (var j = 0; j < this.entitiesToAggregate.length; j++) {
        if (agregatedEntities[i].code === this.entitiesToAggregate[j].code) {
          copyEntitiesToAggregate.splice(j, 1);
          break;
        }
      }
    }
    return agregatedEntities.concat(copyEntitiesToAggregate);
  }

  removeDuplicateEntities(
    aggregatedEntities: EntityInterface[],
  ): EntityInterface[] {
    const seenCodes = new Set<string>();
    return aggregatedEntities.filter((entity) => {
      if (entity.code && seenCodes.has(entity.code)) {
        return false;
      } else {
        if (entity.code) {
          seenCodes.add(entity.code);
        }
        return true;
      }
    });
  }

  checkIsInCurrentAggregatedEntities(entityCode: string): boolean {
    const currentAggregatedEntities =
      JSON.parse(sessionStorage.getItem('currentAggregatedEntities')!) ??
      undefined;
    if (!currentAggregatedEntities) {
      return false;
    }
    return !!currentAggregatedEntities.find(
      (entity: EntityInterface) => entity.code === entityCode,
    );
  }
}
